import { z } from 'zod';

import { API_PATHS, API_ROUTES } from '../../../lib/apiRoutes';
import type { ShopperPreferences } from '../../shopper/types/shopper.types';
import { apiRequest } from '../../../lib/api';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import {
  OrderStatus,
  customerFacingStatus,
  normalizeCanonicalOrderStatus,
  type CustomerFacingOrderStatus
} from '../../../lib/contracts';
import type { CreateOrderPayload, Order, OrderEnvelope, OrdersListEnvelope, AddressChangePreview, ConfirmAddressChangePayload } from '../types/orders.types';
import { checkoutFinalizationPayloadSchema, orderQuoteSnapshotSchema, requiresCheckoutAcknowledgmentRule, requiresLineSubstituteAcknowledgmentRule, requiresRetailerAuthorizationRule } from '../../../../shared/checkoutFinalization.contract';
// Contract markers: requiresCheckoutAcknowledgment lowConfidenceAccepted retailerAuthorizationAccepted

export interface OrderTrackingResponse {
  orderId?: string;
  status?: string;
  shopperId?: string;
  driverId?: string;
  latitude?: number;
  longitude?: number;
  etaMinutes?: number;
  recordedAt?: string;
  data?: {
    orderId?: string;
    status?: string;
    shopperId?: string;
    driverId?: string;
    latitude?: number;
    longitude?: number;
    etaMinutes?: number;
    recordedAt?: string;
  };
}

interface OrderUpdatePayload {
  status?: OrderStatus;
  notes?: string;
  shippingAddress?: string;
  deliveryInstructions?: string;
  paymentMethod?: string;
  tip?: number;
  tipCents?: number;
}

interface OrdersListParams {
  limit?: number;
  skip?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}
const MAX_ORDERS_DATE_RANGE_DAYS = 366;
const ONE_DAY_MS = 1000 * 60 * 60 * 24;

const businessOrderIdSchema = z.string().trim().regex(/^(?:NP|GS)-[A-Z0-9]{8}$/);
const mongoObjectIdSchema = z.string().trim().regex(/^[a-f\d]{24}$/i);
const orderIdSchema = z.union([businessOrderIdSchema, mongoObjectIdSchema]);
const idempotencyKeySchema = z.string().trim().min(8).max(128);
const orderItemSchema = z.object({
  id: z.string().trim().nullish(),
  productId: z.string().trim().nullish(),
  product: z.union([
    z.string().trim(),
    z.object({
      _id: z.string().nullish(),
      id: z.string().nullish(),
      name: z.string().nullish()
    }).passthrough()
  ]).nullish(),
  name: z.string().trim().nullish(),
  price: z.coerce.number().nullish(),
  quantity: z.coerce.number().int().nonnegative().default(1),
  temperaturePreference: z.string().trim().nullish(),
  temperatureClass: z.string().trim().nullish()
}).passthrough();

const itemsNotFoundItemSchema = z.object({
  sku: z.string().trim().nullish(),
  name: z.string().trim().nullish(),
  quantity: z.coerce.number().int().positive().nullish(),
  price: z.coerce.number().nonnegative().nullish(),
  substitutePrice: z.coerce.number().nonnegative().nullish(),
  originalStore: z.string().trim().nullish(),
  attemptedStores: z.array(z.string().trim()).nullish(),
  workflowStatus: z.string().trim().nullish(),
  customerDecision: z.string().trim().nullish(),
  customerDecisionAt: z.union([z.string().trim(), z.date()]).nullish(),
  customerNotifiedAt: z.union([z.string().trim(), z.date()]).nullish(),
  substituteProductId: z.string().trim().nullish(),
  extraStoreId: z.string().trim().nullish(),
  foundAt: z.string().trim().nullish(),
  foundAtTime: z.union([z.string().trim(), z.date()]).nullish(),
  removedAt: z.union([z.string().trim(), z.date()]).nullish()
}).passthrough();

const normalizedOrderSchema = z.object({
  id: z.string().trim().nullish(),
  orderId: z.string().trim().nullish(),
  _id: z.string().trim().nullish(),
  customerId: z.string().trim().nullish(),
  user: z.union([
    z.string().trim(),
    z.object({
      _id: z.string().nullish(),
      id: z.string().nullish(),
      username: z.string().nullish(),
      name: z.string().nullish(),
      displayName: z.string().nullish()
    }).passthrough()
  ]).nullish(),
  shopperId: z.string().trim().nullish(),
  shopper: z.union([
    z.string().trim(),
    z.object({
      _id: z.string().nullish(),
      id: z.string().nullish(),
      name: z.string().nullish(),
      displayName: z.string().nullish()
    }).passthrough()
  ]).nullish(),
  driverId: z.string().trim().nullish(),
  driver: z.union([
    z.string().trim(),
    z.object({
      _id: z.string().nullish(),
      id: z.string().nullish(),
      name: z.string().nullish()
    }).passthrough()
  ]).nullish(),
  items: z.array(orderItemSchema).nullish(),
  orderItems: z.array(orderItemSchema).nullish(),
  total: z.coerce.number().nonnegative().nullish(),
  totalPrice: z.coerce.number().nonnegative().nullish(),
  amount: z.coerce.number().nonnegative().nullish(),
  status: z.string().trim().nullish(),
  internalStatus: z.string().trim().nullish(),
  customerState: z.string().trim().nullish(),
  itemsNotFound: z.array(itemsNotFoundItemSchema).nullish(),
  amountAuthorizedCents: z.coerce.number().int().nonnegative().nullish(),
  amountCapturedCents: z.coerce.number().int().nonnegative().nullish(),
  estimatedFinalCharge: z.coerce.number().nonnegative().nullish(),
  createdAt: z.union([z.string().trim(), z.date()]).nullish(),
  shopperPreferences: z.record(z.unknown()).nullish()
}).passthrough();

const orderStatusSchema = z.union([
  z.nativeEnum(OrderStatus),
  z.literal('CANCELLED'),
  z.literal('CANCELED')
]);

const checkoutItemSchema = z.object({
  productId: z.string().trim().min(1),
  quantity: z.number().int().positive(),
  temperaturePreference: z.enum(['chilled', 'ambient', 'cold', 'either']).optional()
});

const createOrderPayloadSchema = checkoutFinalizationPayloadSchema.superRefine((value, ctx) => {
  requiresCheckoutAcknowledgmentRule(value.quoteSnapshot, ctx);
  requiresRetailerAuthorizationRule(value.quoteSnapshot, ctx);
  requiresLineSubstituteAcknowledgmentRule(value.quoteSnapshot, ctx);
});

const orderEnvelopeSchema = z.union([
  normalizedOrderSchema,
  z.object({ data: normalizedOrderSchema.nullish() }).passthrough()
]);

const listOrdersResponseSchema = z.union([
  z.array(normalizedOrderSchema),
  z
    .object({
      data: z
        .union([
          z.array(normalizedOrderSchema),
          z
            .object({
              orders: z.array(normalizedOrderSchema).nullish(),
              items: z.array(normalizedOrderSchema).nullish(),
              total: z.coerce.number().optional()
            })
            .passthrough()
        ])
        .nullish()
    })
    .passthrough(),
  z
    .object({
      orders: z.array(normalizedOrderSchema).nullish(),
      items: z.array(normalizedOrderSchema).nullish(),
      total: z.coerce.number().optional()
    })
    .passthrough()
]);

const orderUpdatePayloadSchema = z
  .object({
    status: orderStatusSchema.optional()
  })
  .catchall(z.unknown());

const checkoutSessionResponseSchema = z
  .object({
    success: z.boolean().optional(),
    data: z
      .object({
        id: z.string().optional(),
        url: z.string().url().optional(),
        clientSecret: z.string().optional(),
        uiMode: z.enum(['embedded', 'hosted', 'payment_sheet']).optional()
      })
      .passthrough()
      .optional()
  })
  .passthrough();

const checkoutPreviewPayloadSchema = z.object({
  cartItems: z.array(checkoutItemSchema).min(1),
  deliveryAddress: z.union([z.string().trim().min(1), z.record(z.unknown())])
});
const checkoutPreviewResponseSchema = z
  .object({
    success: z.boolean().optional(),
    data: z
      .object({
        subtotal: z.number().nonnegative().optional(),
        deliveryFee: z.number().nonnegative().optional(),
        tax: z.number().nonnegative().optional(),
        total: z.number().nonnegative().optional(),
        selectedStoreId: z.string().optional(),
        quoteExpiresAt: z.string().optional(),
        generatedAt: z.string().trim().datetime({ offset: true }).optional(),
        uncertainty: orderQuoteSnapshotSchema.shape.uncertainty.optional(),
        checkoutAcknowledgment: orderQuoteSnapshotSchema.shape.checkoutAcknowledgment.optional()
      })
      .passthrough()
      .optional()
  })
  .passthrough();

const ordersListParamsSchema = z.object({
  limit: z.number().int().positive().max(500).optional(),
  skip: z.number().int().min(0).optional(),
  status: z.string().trim().min(1).max(64).optional(),
  startDate: z.string().trim().datetime({ offset: true }).or(z.string().trim().date()).optional(),
  endDate: z.string().trim().datetime({ offset: true }).or(z.string().trim().date()).optional()
}).refine((value) => {
  if (!value.startDate || !value.endDate) return true;
  const startDate = new Date(value.startDate);
  const endDate = new Date(value.endDate);
  if (startDate > endDate) return false;
  return (endDate.getTime() - startDate.getTime()) / ONE_DAY_MS <= MAX_ORDERS_DATE_RANGE_DAYS;
}, {
  message: `startDate must be before or equal to endDate and date range cannot exceed ${MAX_ORDERS_DATE_RANGE_DAYS} days`
});



type CheckoutPreviewPayload = z.input<typeof checkoutPreviewPayloadSchema>;
type CheckoutPreviewResponse = z.output<typeof checkoutPreviewResponseSchema>;

const normalizeFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : Number(value ?? fallback);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeOptionalFiniteNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseOrderEnvelope = (response: unknown, endpoint: string): OrderEnvelope =>
  validateWithSchema(orderEnvelopeSchema, response, endpoint) as OrderEnvelope;

const parseOrdersListEnvelope = (response: unknown): OrdersListEnvelope =>
  validateWithSchema(listOrdersResponseSchema, response, API_ROUTES.v1.orders) as OrdersListEnvelope;

const resolveOrderId = (order: z.infer<typeof normalizedOrderSchema>): string =>
  String(order.orderId || order.id || order._id || '').trim();

const normalizeOrder = (order: unknown): Order => {
  const parsed = validateWithSchema<z.output<typeof normalizedOrderSchema>>(normalizedOrderSchema, order, API_ROUTES.v1.orders);
  const resolvedOrderId = resolveOrderId(parsed);
  if (!resolvedOrderId) {
    throw new Error('Order payload missing id/orderId');
  }

  const rawItems = parsed.items || parsed.orderItems || [];
  const normalizedItems = rawItems
    .map((item) => {
      const preferenceRaw = String(item.temperaturePreference || '').trim().toLowerCase();
      const classRaw = String(item.temperatureClass || '').trim().toLowerCase();
      const resolvedProdId = typeof item.product === 'string'
        ? item.product
        : (item.product && typeof item.product === 'object'
            ? String((item.product as { _id?: unknown; id?: unknown })._id || (item.product as { _id?: unknown; id?: unknown }).id || '')
            : '');
      return {
        id: String(item.id || item.productId || resolvedProdId || '').trim(),
        quantity: normalizeFiniteNumber(item.quantity, 1),
        name: typeof item.name === 'string' ? item.name.trim() : undefined,
        price: normalizeOptionalFiniteNumber(item.price),
        temperaturePreference:
          preferenceRaw === 'chilled' || preferenceRaw === 'cold'
            ? ('chilled' as const)
            : preferenceRaw === 'ambient' || preferenceRaw === 'either'
              ? ('ambient' as const)
              : undefined,
        temperatureClass:
          classRaw === 'chilled' || classRaw === 'cold'
            ? ('chilled' as const)
            : classRaw === 'ambient'
              ? ('ambient' as const)
              : undefined
      };
    })
    .filter((item) => item.id && item.quantity > 0);

  const internalNorm = normalizeCanonicalOrderStatus(
    parsed.internalStatus != null && String(parsed.internalStatus).trim() !== ''
      ? parsed.internalStatus
      : parsed.status
  );
  const cfRaw = parsed.customerState?.trim();
  const customerStateResolved = (
    cfRaw && ['PENDING', 'CONFIRMED', 'SHOPPING', 'AWAITING_DECISION', 'OUT_FOR_DELIVERY', 'ARRIVING', 'DELIVERED', 'REFUNDED', 'CANCELED', 'PAYMENT_ISSUE', 'CLOSED'].includes(cfRaw)
      ? cfRaw
      : customerFacingStatus(internalNorm)
  ) as CustomerFacingOrderStatus;

  return {
    ...(parsed as unknown as Order),
    id: resolvedOrderId,
    legacyId: String(parsed._id || '').trim() || undefined,
    customerId:
      (typeof parsed.user === 'object' && parsed.user
        ? String((parsed.user as Record<string, unknown>).id || (parsed.user as Record<string, unknown>)._id || parsed.customerId || 'GUEST').trim()
        : String(parsed.customerId || parsed.user || 'GUEST').trim()) || 'GUEST',
    shopperId:
      (typeof parsed.shopper === 'object' && parsed.shopper
        ? String((parsed.shopper as Record<string, unknown>).id || (parsed.shopper as Record<string, unknown>)._id || parsed.shopperId || '').trim()
        : String(parsed.shopperId || parsed.shopper || '').trim()) || undefined,
    driverId:
      (typeof parsed.driver === 'object' && parsed.driver
        ? String((parsed.driver as Record<string, unknown>).id || (parsed.driver as Record<string, unknown>)._id || parsed.driverId || '').trim()
        : String(parsed.driverId || parsed.driver || '').trim()) || undefined,
    internalStatus: internalNorm,
    customerState: customerStateResolved,
    status: internalNorm,
    items: normalizedItems,
    shopperPreferences: parsed.shopperPreferences as unknown as ShopperPreferences | undefined,
    amount: normalizeFiniteNumber(parsed.amount ?? parsed.totalPrice ?? parsed.total ?? 0),
    amountAuthorizedCents: normalizeOptionalFiniteNumber(parsed.amountAuthorizedCents),
    amountCapturedCents: normalizeOptionalFiniteNumber(parsed.amountCapturedCents),
    authorizedAmount: typeof parsed.amountAuthorizedCents === 'number' ? parsed.amountAuthorizedCents / 100 : undefined,
    capturedAmount: typeof parsed.amountCapturedCents === 'number' ? parsed.amountCapturedCents / 100 : undefined,
    estimatedFinalCharge: normalizeFiniteNumber(parsed.estimatedFinalCharge ?? 0)
  };
};

const normalizeOrderListEnvelope = (parsed: OrdersListEnvelope): OrdersListEnvelope | Order[] => {
  if (Array.isArray(parsed)) {
    return parsed.map(normalizeOrder);
  }

  const payload = parsed.data;
  if (Array.isArray(payload)) {
    return { ...parsed, data: payload.map(normalizeOrder) };
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const updated: Record<string, unknown> = { ...record };
    if (Array.isArray(record.orders)) {
      updated.orders = record.orders.map(normalizeOrder);
    }
    if (Array.isArray(record.items)) {
      updated.items = record.items.map(normalizeOrder);
    }
    return { ...parsed, data: updated } as OrdersListEnvelope;
  }

  return parsed;
};

const withQueryParams = (basePath: string, params?: OrdersListParams): string => {
  if (!params) return basePath;
  const validated = validateWithSchema<z.output<typeof ordersListParamsSchema>>(ordersListParamsSchema, params, basePath);
  const normalizedStartDate = validated.startDate ? new Date(validated.startDate).toISOString() : undefined;
  const normalizedEndDate = validated.endDate ? new Date(validated.endDate).toISOString() : undefined;

  if (normalizedStartDate && normalizedEndDate) {
    const rangeDays = (new Date(normalizedEndDate).getTime() - new Date(normalizedStartDate).getTime()) / ONE_DAY_MS;
    if (rangeDays > MAX_ORDERS_DATE_RANGE_DAYS) {
      throw new Error(`Date range cannot exceed ${MAX_ORDERS_DATE_RANGE_DAYS} days`);
    }
  }

  const search = new URLSearchParams();
  if (validated.limit) search.set('limit', String(validated.limit));
  if (validated.skip !== undefined) search.set('skip', String(validated.skip));
  if (validated.status) search.set('status', validated.status);
  if (normalizedStartDate) search.set('startDate', normalizedStartDate);
  if (normalizedEndDate) search.set('endDate', normalizedEndDate);
  const query = search.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const ordersApi = {
  list: async (params?: OrdersListParams) => {
    const endpoint = withQueryParams(API_ROUTES.v1.orders, params);
    const response = await apiRequest<unknown>(endpoint);
    const parsed = parseOrdersListEnvelope(response);
    return normalizeOrderListEnvelope(parsed);
  },
  /** One page of the caller-scoped order list plus the server's total match count. */
  listPage: async (params: OrdersListParams): Promise<{ orders: Order[]; total: number }> => {
    const endpoint = withQueryParams(API_ROUTES.v1.orders, params);
    const response = await apiRequest<unknown>(endpoint);
    const normalized = normalizeOrderListEnvelope(parseOrdersListEnvelope(response));
    if (Array.isArray(normalized)) return { orders: normalized, total: normalized.length };
    const data = normalized.data;
    if (Array.isArray(data)) return { orders: data, total: data.length };
    const orders = Array.isArray(data?.orders) ? data.orders : Array.isArray(data?.items) ? data.items : [];
    const total = Number(data?.total);
    return { orders, total: Number.isFinite(total) ? total : orders.length };
  },
  /** Staff lookup of one customer's most recent orders (server-scoped, newest first, max 100). */
  searchByCustomer: async (customerId: string, limit = 100): Promise<Order[]> => {
    const response = await apiRequest<unknown>(API_PATHS.orders.search, {
      query: {
        customerId: mongoObjectIdSchema.parse(customerId),
        limit: Math.min(100, Math.max(1, Math.floor(limit))),
        sortBy: 'createdAt',
        sortDir: 'desc'
      }
    });
    const normalized = normalizeOrderListEnvelope(parseOrdersListEnvelope(response));
    if (Array.isArray(normalized)) return normalized;
    const data = normalized.data;
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.orders) ? data.orders : [];
  },
  getById: async (orderId: string) => {
    const endpoint = API_PATHS.orders.byId(orderIdSchema.parse(orderId));
    const response = await apiRequest<unknown>(endpoint);
    const parsed = parseOrderEnvelope(response, endpoint);
    if (parsed && typeof parsed === 'object' && 'data' in parsed) {
      return {
        ...parsed,
        data: parsed.data ? normalizeOrder(parsed.data) : null
      };
    }
    return normalizeOrder(parsed);
  },
  getEvents: async (orderId: string) => {
    const endpoint = API_PATHS.orders.events(orderIdSchema.parse(orderId));
    const response = await apiRequest<{
      success?: boolean;
      data?: { events?: unknown[] } | unknown[];
      events?: unknown[];
    }>(endpoint);
    const nested = response?.data;
    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === 'object' && Array.isArray((nested as { events?: unknown[] }).events)) {
      return (nested as { events: unknown[] }).events;
    }
    if (Array.isArray(response?.events)) return response.events;
    return [];
  },
  getDeliveryContact: async (orderId: string) => {
    const endpoint = `${API_PATHS.orders.deliveryContact(orderIdSchema.parse(orderId))}`;
    return apiRequest<unknown>(endpoint);
  },
  getTracking: async (orderId: string): Promise<OrderTrackingResponse> => {
    const endpoint = API_PATHS.orders.tracking(orderIdSchema.parse(orderId));
    return apiRequest<OrderTrackingResponse>(endpoint);
  },
  update: async (id: string, body: OrderUpdatePayload, idempotencyKey: string) => {
    const endpoint = API_PATHS.orders.byId(orderIdSchema.parse(id));
    const response = await apiRequest<unknown>(endpoint, {
      method: 'PATCH',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema(orderUpdatePayloadSchema, body, endpoint))
    });

    const parsed = parseOrderEnvelope(response, endpoint);
    if (parsed && typeof parsed === 'object' && 'data' in parsed) {
      return {
        ...parsed,
        data: parsed.data ? normalizeOrder(parsed.data) : null
      };
    }
    return normalizeOrder(parsed);
  },
  previewCheckout: async (body: CheckoutPreviewPayload, idempotencyKey: string, signal?: AbortSignal): Promise<CheckoutPreviewResponse> => {
    const response = await apiRequest<unknown>(API_PATHS.checkout.preview, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema(checkoutPreviewPayloadSchema, body, API_PATHS.checkout.preview)),
      signal
    });

    return validateWithSchema(checkoutPreviewResponseSchema, response, API_PATHS.checkout.preview);
  },
  create: async (body: CreateOrderPayload, idempotencyKey: string) => {
    const validatedBody = validateWithSchema(createOrderPayloadSchema, body, API_PATHS.orders.root);
    const response = await apiRequest<unknown>(API_PATHS.orders.root, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validatedBody)
    });

    const parsed = parseOrderEnvelope(response, API_PATHS.orders.root);
    if (parsed && typeof parsed === 'object' && 'data' in parsed) {
      const data = (parsed as { data?: unknown }).data as
        | { order?: unknown; checkoutSession?: unknown }
        | null
        | undefined;
      if (data && typeof data === 'object' && 'order' in data) {
        return {
          ...parsed,
          data: {
            ...data,
            order: data.order ? normalizeOrder(data.order) : null
          }
        };
      }
      return {
        ...parsed,
        data: parsed.data ? normalizeOrder(parsed.data) : null
      };
    }
    return normalizeOrder(parsed);
  },
  createCheckoutSession: async (
    orderId: string,
    idempotencyKey: string,
    mandates?: {
      uiMode?: 'embedded' | 'hosted' | 'payment_sheet';
      checkoutMandate?: {
        payload: {
          orderId: string;
          maxAmountCents: number;
          agentId: string;
          timestamp: number;
        };
        signature: string;
      };
      ap2CheckoutMandate?: {
        checkoutJwt: string;
        checkout_hash: string;
        payload: {
          orderId: string;
          maxAmountCents: number;
          agentId: string;
          timestamp: number;
        };
        signature: string;
      };
    }
  ) => {
    const endpoint = API_PATHS.payments.checkoutSession(orderIdSchema.parse(orderId));
    const body = JSON.stringify({
      uiMode: mandates?.uiMode || 'embedded',
      ...(mandates?.checkoutMandate ? { checkoutMandate: mandates.checkoutMandate } : {}),
      ...(mandates?.ap2CheckoutMandate ? { ap2CheckoutMandate: mandates.ap2CheckoutMandate } : {})
    });
    const response = await apiRequest<unknown>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body
    });

    return validateWithSchema(checkoutSessionResponseSchema, response, endpoint);
  },
  decideExtraStore: async (orderId: string, sku: string, approved: boolean, idempotencyKey: string) => {
    const endpoint = `${API_PATHS.orders.byId(orderId)}/shopping/missingItems/${encodeURIComponent(sku)}/extraStoreApproval`;
    return await apiRequest<OrderEnvelope>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({ approved })
    });
  },
  skipExtraStore: async (orderId: string, reason?: string, idempotencyKey?: string) => {
    const endpoint = `${API_PATHS.orders.byId(orderIdSchema.parse(orderId))}/skip-extra-store`;
    const key = idempotencyKey || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `skip-${Date.now()}`);
    return await apiRequest<{
      success?: boolean;
      data?: {
        orderId: string;
        skipped: boolean;
        feeToRefundCents: number;
        refundIssued: boolean;
        customerNotice: string;
      };
    }>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(key),
      requireIdempotencyKey: true,
      body: JSON.stringify({ reason: reason || 'Found all items at first store' })
    });
  },
  /**
   * L2 (hard launch v2): customer-driven cancel/reschedule.
   *
   * Backend wraps these in strictIdempotency keyed by orderId+userId, so
   * a double-tap on the FE button is safe. We expose them on ordersApi
   * so the shopper page doesn't need to know the URL shape.
   */
  customerCancel: async (orderId: string, reason: string, idempotencyKey: string) => {
    const endpoint = `${API_PATHS.orders.byId(orderIdSchema.parse(orderId))}/cancel`;
    return await apiRequest<OrderEnvelope>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({ reason: String(reason || '').trim().slice(0, 500) })
    });
  },
  adjustTip: async (
    orderId: string,
    body: { tipCents: number; reason?: string; note?: string; accolades?: string[] },
    idempotencyKey: string
  ): Promise<{
    orderId?: string;
    tipCents?: number;
    previousTipCents?: number;
    direction?: string;
    reason?: string | null;
    accolades?: string[];
    tipAdjustedAt?: string | Date;
    unchanged?: boolean;
  }> => {
    const endpoint = `${API_PATHS.orders.byId(orderIdSchema.parse(orderId))}/tip`;
    const response = await apiRequest<{
      success?: boolean;
      data?: {
        orderId?: string;
        tipCents?: number;
        previousTipCents?: number;
        direction?: string;
        reason?: string | null;
        accolades?: string[];
        tipAdjustedAt?: string | Date;
        unchanged?: boolean;
      };
  tipCents?: number;
  unchanged?: boolean;
    }>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({
        tipCents: Math.max(0, Math.round(Number(body.tipCents || 0))),
        ...(body.reason ? { reason: body.reason } : {}),
        ...(body.note ? { note: String(body.note).trim().slice(0, 500) } : {}),
        ...(Array.isArray(body.accolades) ? { accolades: body.accolades } : {})
      })
    });
    const fromData =
      response?.data && typeof response.data === 'object'
        ? response.data
        : null;
    const { data: _ignored, success: _s, ...rest } = (response || {}) as {
      data?: unknown;
      success?: boolean;
      tipCents?: number;
      unchanged?: boolean;
      orderId?: string;
      previousTipCents?: number;
      direction?: string;
      reason?: string | null;
      tipAdjustedAt?: string | Date;
    };
    const result = fromData || (Object.keys(rest).length ? rest : null);
    if (!result || !Number.isFinite(Number((result as { tipCents?: unknown }).tipCents))) {
      throw new Error('Tip update was not confirmed by the server. Try again.');
    }
    return result as {
      tipCents: number;
      unchanged?: boolean;
      orderId?: string;
      previousTipCents?: number;
      direction?: string;
      reason?: string | null;
      tipAdjustedAt?: string | Date;
    };
  },
  customerReschedule: async (orderId: string, deliveryWindowId: string, idempotencyKey: string) => {
    const endpoint = `${API_PATHS.orders.byId(orderIdSchema.parse(orderId))}/reschedule`;
    return await apiRequest<OrderEnvelope>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({ deliveryWindowId: String(deliveryWindowId || '').trim() })
    });
  },
  previewAddressChange: async (orderId: string, newAddress: string, idempotencyKey?: string): Promise<AddressChangePreview> => {
    const endpoint = `${API_PATHS.orders.byId(orderIdSchema.parse(orderId))}/address/preview`;
    const response = await apiRequest<{ success?: boolean; data?: AddressChangePreview } | AddressChangePreview>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKey ? idempotencyKeySchema.parse(idempotencyKey) : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `preview-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`),
      requireIdempotencyKey: true,
      body: JSON.stringify({ newAddress: String(newAddress || '').trim() })
    });
    const preview =
      response && typeof response === 'object' && 'data' in response && response.data
        ? response.data
        : (response as AddressChangePreview | null);
    const relocationFeeCents = Number(preview?.relocationFeeCents);
    const newTotalCents = Number(preview?.newTotalCents);
    const confirmedAddress = String(preview?.newAddress || '').trim();
    if (
      !preview ||
      !confirmedAddress ||
      !Number.isFinite(relocationFeeCents) ||
      relocationFeeCents < 0 ||
      !Number.isFinite(newTotalCents)
    ) {
      throw new Error('Address change quote was incomplete. Try again.');
    }
    return {
      ...preview,
      newAddress: confirmedAddress,
      relocationFeeCents,
      newTotalCents
    };
  },
  confirmAddressChange: async (
    orderId: string,
    payload: ConfirmAddressChangePayload,
    idempotencyKey: string
  ): Promise<OrderEnvelope> => {
    const endpoint = `${API_PATHS.orders.byId(orderIdSchema.parse(orderId))}/address`;
    const response = await apiRequest<unknown>(endpoint, {
      method: 'POST',
      idempotencyKey: idempotencyKeySchema.parse(idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({
        newAddress: String(payload.newAddress || '').trim(),
        deliveryInstructions: payload.deliveryInstructions ? String(payload.deliveryInstructions).trim() : undefined,
        acceptedFeeCents: Math.max(0, Math.round(Number(payload.acceptedFeeCents || 0)))
      })
    });
    const parsed = parseOrderEnvelope(response, endpoint);
    if (parsed && typeof parsed === 'object' && 'data' in parsed) {
      return {
        ...parsed,
        data: parsed.data ? normalizeOrder(parsed.data) : null
      };
    }
    return normalizeOrder(parsed);
  }
};

export type { AddressChangePreview } from "../types/orders.types";
